import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import "../customers/customers.css";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import {
  Box,
  Button,
  Typography,
  Switch,
  FormControlLabel,
  FormControl,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from "@mui/material";
import { FaX, FaUserPlus, FaUserPen, FaCheck } from "react-icons/fa6";
import { toast } from "react-toastify";
import { DataTable } from "../../components/DataTable";
import DealerForm from "./DealerForm";
import { useDrawer } from "../../components/DrawerContext";
import axios from "axios";
import config from "../../config/config";
import { useAuth } from '../login/AuthContext';
import { AddCircleOutline } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import IconActionsRenderer from "../../components/IconActionsRenderer.jsx";

// Create a DrawerContext provider specifically for dealers
export const DealersContext = React.createContext({
  isDrawerOpen: false,
  toggleDrawer: () => {},
  editingDealer: null,
  openDrawerWithDealer: () => {},
  openDrawerForAddDealer: () => {}
});

export const DealersProvider = ({ children }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingDealer, setEditingDealer] = useState(null);

  const toggleDrawer = useCallback(() => {
    setIsDrawerOpen(prev => !prev);
    if (isDrawerOpen) {
      // Clear editing dealer when closing drawer
      setEditingDealer(null);
    }
  }, [isDrawerOpen]);

  const openDrawerWithDealer = useCallback((dealer) => {
    // console.log("Opening drawer with dealer:", dealer);
    setEditingDealer(dealer);
    setIsDrawerOpen(true);
  }, []);

  const openDrawerForAddDealer = useCallback(() => {
    setEditingDealer(null);
    setIsDrawerOpen(true);
  }, []);

  const value = useMemo(() => ({
    isDrawerOpen,
    toggleDrawer,
    editingDealer,
    openDrawerWithDealer,
    openDrawerForAddDealer
  }), [isDrawerOpen, toggleDrawer, editingDealer, openDrawerWithDealer, openDrawerForAddDealer]);

  return (
    <DealersContext.Provider value={value}>
      {children}
    </DealersContext.Provider>
  );
};

export const useDealersDrawer = () => React.useContext(DealersContext);

export const Dealers = () => {
  const { token, userData, hasPermission } = useAuth();
  const [searchText, setSearchText] = useState("");
  const [filterStatus, setFilterStatus] = useState("aktif");
  const [isLoading, setIsLoading] = useState(false);

  // Create a local drawer state to avoid conflicts with other components
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingDealer, setEditingDealer] = useState(null);

  const toggleDrawer = useCallback(() => {
    setIsDrawerOpen(prev => !prev);
    if (isDrawerOpen) {
      // Clear editing dealer when closing drawer
      setEditingDealer(null);
    }
  }, [isDrawerOpen]);

  const openDrawerWithDealer = useCallback((dealer) => {
    // console.log("Opening drawer with dealer:", dealer);
    setEditingDealer(dealer);
    setIsDrawerOpen(true);
  }, []);

  const openDrawerForAddDealer = useCallback(() => {
    setEditingDealer(null);
    setIsDrawerOpen(true);
  }, []);

  const searchTimeoutRef = useRef(null);
  const dataFetchTimeoutRef = useRef(null);
  const dataTableRef = useRef(null);
  const isInitialMount = useRef(true);
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteDialerId, setDeleteDialerId] = useState(null);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [restoreDialerId, setRestoreDialerId] = useState(null);

  // CustomQueryParams'ı useMemo ile oluşturup gereksiz render'ları önlüyoruz
  const queryParams = useMemo(() => ({ status: filterStatus }), [filterStatus]);

  // Arama işlemi için debounce uygulanmış fonksiyon
  const handleSearch = useCallback((value) => {
    setSearchText(value);

    // Önceki zamanlayıcıyı temizle
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // 3 karakterden az arama yaparken loading gösterme
    setIsLoading(value.length >= 3);
  }, []);

  // Status değişikliği için işleyici
  const handleStatusChange = useCallback((event) => {
    const newStatus = event.target.checked ? "aktif" : "pasif";
    setIsLoading(true);
    setFilterStatus(newStatus);
    setSearchText(""); // Arama kutusunu temizle
  }, []);

  // Veri yükleme için tek bir useEffect - hem queryParams hem searchText değişikliklerini izler
  useEffect(() => {
    // İlk render'da çalışmasını engelle (isInitialMount true ise atla)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Önceki veri yükleme zamanlayıcısını temizle
    if (dataFetchTimeoutRef.current) {
      clearTimeout(dataFetchTimeoutRef.current);
    }

    // Yeni bir zamanlayıcı oluştur - gereksiz istekleri önlemek için
    dataFetchTimeoutRef.current = setTimeout(() => {
      // console.log("Veri yükleniyor - Tetikleyici:", {
      //   filterStatus,
      //   searchText,
      //   searchLength: searchText?.length || 0
      // });

      if (dataTableRef.current?.refreshData) {
        dataTableRef.current.refreshData();
      }
    }, 300);

    // Temizlik fonksiyonu
    return () => {
      if (dataFetchTimeoutRef.current) {
        clearTimeout(dataFetchTimeoutRef.current);
      }
    };
  }, [queryParams, searchText]);

  // Component unmount olduğunda zamanlayıcıları temizle
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (dataFetchTimeoutRef.current) {
        clearTimeout(dataFetchTimeoutRef.current);
      }
    };
  }, []);

  // Bayi oluşturulduğunda tabloyu yenile ve drawer'ı kapat
  const handleDealerCreated = useCallback(() => {
    if (dataTableRef.current?.refreshData) {
      dataTableRef.current.refreshData();
    }
    toggleDrawer();
  }, [toggleDrawer]);

  // API İstekleri için axios instance oluşturma
  const axiosInstance = useCallback(() => {
    return axios.create({
      baseURL: config.baseURL,
      headers: { Authorization: `Bearer ${token}` }
    });
  }, [token]);

  // CRUD İşlemleri
  const handleEditDealer = useCallback(async (dealerId) => {
    if (!userData || !token) {
      toast.error("Yetkilendirme hatası");
      return;
    }

    try {
      setIsLoading(true);
      const api = axiosInstance();
      const response = await api.get(`/bayiler/${dealerId}`);

      // Make sure we're getting data in the expected format
      if (response.data) {
        // console.log("Fetched dealer data:", response.data);
        openDrawerWithDealer(response.data);
      } else {
        throw new Error("Bayi bilgisi alınamadı");
      }
    } catch (error) {
      console.error("Bayi detay hatası:", error);
      toast.error("Bayi detayları alınamadı");
    } finally {
      setIsLoading(false);
    }
  }, [userData, token, openDrawerWithDealer, axiosInstance]);

  const handleDeleteDealer = useCallback(async (dealerId) => {
    try {
      setIsLoading(true);
      const api = axiosInstance();
      await api.delete(`/bayiler/${dealerId}`);
      toast.success("Bayi başarıyla silindi");

      // Silme işlemi sonrası dataTable'ı yeniliyoruz
      if (dataTableRef.current?.refreshData) {
        dataTableRef.current.refreshData();
      }

      return true;
    } catch (error) {
      console.error("Silme hatası:", error);
      toast.error("Bayi silinemedi");
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [axiosInstance]);

  const handleRestoreDealer = useCallback(async (dealerId) => {
    try {
      setIsLoading(true);
      const api = axiosInstance();
      await api.put(`/bayiler/restore/${dealerId}`);

      // Geri yükleme işlemi sonrası dataTable'ı yeniliyoruz
      if (dataTableRef.current?.refreshData) {
        dataTableRef.current.refreshData();
      }

      return true;
    } catch (error) {
      console.error("Geri yükleme hatası:", error);
      toast.error("Bayi geri yüklenemedi");
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [axiosInstance]);

  const handleViewDealer = useCallback((dealerId) => {
    navigate(`/bayiler/${dealerId}`);
  }, [navigate]);

  const handleOpenDeleteModal = useCallback((dealerId) => {
    setDeleteDialerId(dealerId);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDeleteDialerId(null);
  }, []);

  const handleOpenRestoreModal = useCallback((dealerId) => {
    setRestoreDialerId(dealerId);
    setIsRestoreModalOpen(true);
  }, []);

  const handleCloseRestoreModal = useCallback(() => {
    setIsRestoreModalOpen(false);
    setRestoreDialerId(null);
  }, []);

  // Bayiler için özel sütun tanımları
  const dealerColumnDefs = useMemo(() => [
    {
      headerName: 'İşlemler',
      field: 'actions',
      maxWidth: 130,
      minWidth: 130,
      sortable: false,
      filter: false,
      cellRenderer: IconActionsRenderer,
      pinned: 'left',
      cellClass: 'action-cell-center'
    },
    {
      headerName: 'Bayi Türü',
      field: 'BayiTuru',
      sortable: true,
      filter: true,
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => (params.value && String(params.value).trim()) ? params.value : 'Belirtilmemiş',
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true
      }
    },
    {
      headerName: 'Bayi Kodu',
      field: 'Kodu',
      sortable: true,
      filter: true,
      flex: 1,
      minWidth: 120,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true
      }
    },
    {
      headerName: 'Bayi Adı',
      field: 'BayiAdi',
      sortable: true,
      filter: true,
      flex: 2,
      minWidth: 200,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
      }
    },
    {
      headerName: 'Unvan',
      field: 'Unvan',
      sortable: true,
      filter: true,
      flex: 2,
      minWidth: 350,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
      }
    },
    {
      headerName: 'Yetkili',
      field: 'Yetkili',
      sortable: true,
      filter: true,
      flex: 1,
      minWidth: 120,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true
      }
    },
    {
      headerName: 'Email',
      field: 'EPosta',
      sortable: true,
      filter: true,
      flex: 1.5,
      minWidth: 180,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true
      }
    },
    {
      headerName: 'Telefon',
      field: 'Tel',
      sortable: true,
      filter: true,
      flex: 1,
      minWidth: 100,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true
      }
    },
    {
      headerName: 'Adres',
      field: 'Adres',
      sortable: true,
      filter: true,
      flex: 3,
      minWidth: 300,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true
      }
    }
  ], []);

  // DataTable'a geçirilecek özel context değerleri
  const customContext = useMemo(() => ({
    showRestore: filterStatus !== "aktif",
    showView: filterStatus === "aktif",
    showEdit: filterStatus === "aktif",
    showDelete: filterStatus === "aktif",
    onView: handleViewDealer,
    onRestore: handleOpenRestoreModal,
    handleEditOrder: handleEditDealer, // Geriye dönük uyumluluk için
    handleEditItem: handleEditDealer,
    handleOpenDeleteModal: handleOpenDeleteModal,
    hasPermission
  }), [filterStatus, handleViewDealer, handleOpenRestoreModal, handleEditDealer, handleOpenDeleteModal, hasPermission]);

  if (!userData || !token) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="cari-listesi-container">
      <div className="header">
        <h1>Bayiler</h1>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Gelişmiş Arama (en az 3 karakter)"
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          className="search-input"
        />

        <FormControl component="fieldset" style={{ marginLeft: '10px' }}>
          <FormControlLabel
            control={
              <Switch
                checked={filterStatus === "aktif"}
                onChange={handleStatusChange}
                color="primary"
              />
            }
            label={filterStatus === "aktif" ? "Aktif Bayiler" : "Pasif Bayiler"}
          />
        </FormControl>

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircleOutline />}
          onClick={openDrawerForAddDealer}
          sx={{ ml: 2 }}
          size="medium"
          disabled={isLoading}
        >
          Ekle
        </Button>
      </div>

      <DataTable
        ref={dataTableRef}
        searchText={searchText}
        tableType="custom"
        columnDefs={dealerColumnDefs}
        apiEndpoint="/bayiler"
        searchEndpoint="/bayiler/search"
        onEditAction={handleEditDealer}
        onDeleteAction={handleDeleteDealer}
        onRowDoubleClicked={(e) => e.data?.Id != null && handleViewDealer(e.data.Id)}
        deleteConfirmMessage="Bu bayiyi silmek istediğinizden emin misiniz?"
        onLoadingChange={setIsLoading}
        customContext={customContext}
        customQueryParams={queryParams}
        idField="Id"
      />

      <Drawer
        open={isDrawerOpen}
        onClose={toggleDrawer}
        direction="right"
        className="custom-drawer"
        duration={120}
        zIndex={600}
      >
        <Box
          sx={{
            height: '100%',
            overflowY: 'auto',
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={{
              p: 2.5,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              position: 'sticky',
              top: 0,
              zIndex: 1,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="h6"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '1rem'
                }}
              >
                {editingDealer ? (
                  <FaUserPen style={{ fontSize: "20px", color: "rgba(0,0,0,0.54)" }} />
                ) : (
                  <FaUserPlus style={{ fontSize: "20px", color: "rgba(0,0,0,0.54)" }} />
                )}
                {editingDealer ? "Bayi Düzenle" : "Yeni Bayi Ekle"}
              </Typography>
              <Box display="flex" gap={0.5}>
                <Button
                  type="submit"
                  form="bayikaydetform"
                  variant="text"
                  size="medium"
                  sx={{
                    minWidth: 'auto',
                    p: 1,
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      color: 'text.primary'
                    }
                  }}
                >
                  <FaCheck style={{ fontSize: "18px" }} />
                </Button>
                <Button
                  variant="text"
                  size="medium"
                  onClick={toggleDrawer}
                  sx={{
                    minWidth: 'auto',
                    p: 1,
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      color: 'text.primary'
                    }
                  }}
                >
                  <FaX style={{ fontSize: "18px" }} />
                </Button>
              </Box>
            </Box>
          </Box>
          <Box sx={{ flex: 1, p: 2.5, pt: 2 }}>
            <DealerForm
              onDealerCreated={handleDealerCreated}
              editingDealer={editingDealer}
              fetchDealers={() => {
                if (dataTableRef.current?.refreshData) {
                  dataTableRef.current.refreshData();
                }
              }}
              key={editingDealer ? `dealer-${editingDealer.Id}` : 'new-dealer'}
            />
          </Box>
        </Box>
      </Drawer>

      {/* Silme Modalı */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {"Silme İşlemi"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Bu bayiyi silmek istediğinizden emin misiniz?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} color="primary">
            İptal
          </Button>
          <Button
            onClick={() => {
              handleDeleteDealer(deleteDialerId)
                .then(() => handleCloseDeleteModal())
                .catch(err => console.error("Silme hatası:", err));
            }}
            color="error"
            autoFocus
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Geri Yükleme Modalı */}
      <Dialog
        open={isRestoreModalOpen}
        onClose={handleCloseRestoreModal}
        aria-labelledby="restore-dialog-title"
        aria-describedby="restore-dialog-description"
      >
        <DialogTitle id="restore-dialog-title">
          {"Geri Yükleme İşlemi"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="restore-dialog-description">
            Bu bayiyi geri yüklemek istediğinizden emin misiniz?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRestoreModal} color="error">
            İptal
          </Button>
          <Button
            onClick={() => {
              handleRestoreDealer(restoreDialerId)
                .then(() => {
                  handleCloseRestoreModal();
                  toast.success("Bayi başarıyla geri yüklendi");
                })
                .catch(err => {
                  console.error("Geri yükleme hatası:", err);
                  toast.error("Bayi geri yüklenemedi");
                });
            }}
            color="primary"
            autoFocus
          >
            Geri Yükle
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Dealers;